/**
 * 후불 세션 전체 결제 패널 (#661 — ChargeSessionModal 로직 이관, 페이지 임베드용).
 *
 * 테이블 세션의 미결제 후불 주문서(N건)를 1회 카드 입력 + 1회 Bloomin 3DS 로
 * 청구한다 (`POST /api/v1/table-sessions/{token}/charge`). admin R5 가 내려준 Bloomin 3DS
 * `challengeUrl` 을 신뢰 검증 후 발급은행 인증 페이지로 redirect 한다.
 *
 * ## #661: 모달 → 전용 페이지 전환
 *   기존 ChargeSessionModal 은 주문내역 위에 떠서 "주문 내역을 보면서 팁을 입력"하는
 *   흐름이 불가능했다. 보정치/0원 분기/fail-closed/카드 폼/challenge redirect 로직을
 *   본 패널로 이관하고, SessionChargePage 가 주문별 라인아이템과 함께 렌더한다.
 *   모달 chrome(오버레이/ESC/닫기)은 페이지 라우팅(뒤로가기)으로 대체되어 제거됐다.
 *
 * ## #603 [4] → #654 — 팁/포인트 단일 order adjustments (§0(D))
 *   식후 정산 팁/포인트는 **단일 order 전액**으로만 부여한다(비례배분 없음):
 *     `adjustments[String(adjustmentOrderId)] = { tip_amount?, points_amount? }`
 *   #654: 세션 미결제가 **정확히 1건**일 때만 `adjustmentOrderId` 가 주어진다(orders[0].id).
 *   N건이면 null — 보정치 귀속 주문이 결정 불가이므로 팁/포인트 입력 UI 자체를 숨기고
 *   adjustments 를 전송하지 않는다(fail-safe — 임의로 첫 주문에 부여하는 silent 동작 금지).
 *   계산(FE 미리보기, *서버 권위*): `청구액 = 세션소계(totalAmount) + 팁 − 포인트`.
 *     - 청구액 > 0  → 카드 폼 + 보정치 동봉
 *     - 청구액 === 0 → 카드 숨김 + "전액 결제 확정"(cardless) → 빈 카드 + 보정치만
 *   게스트(비회원)는 PointsSection disabled (포인트 사용 불가 — 회원만).
 *
 * ## #1857 — N건 합산결제 팁 (feature-gate, admin 계약 병렬 진행)
 *   N건에서도 팁을 받기로 결정됨(2026-06-10 사용자 확정). admin #1857 이
 *   `session_adjustments: { tip_amount }` (additive — BE 가 주문별 비례 배분) 계약을
 *   병렬 구현 중이라, FE 는 구조만 갖추고 **SESSION_BUNDLE_TIP_ENABLED=false 로 비활성**:
 *     - 단건(adjustmentOrderId 有): 기존 per-order `adjustments` 경로 그대로 (팁+포인트).
 *     - N건 + 게이트 ON: 팁 섹션만 렌더(포인트는 귀속 주문 필요라 계속 숨김) →
 *       `session_adjustments.tip_amount` 전송. per-order `adjustments` 와 **배타**(동시 전송 금지).
 *     - N건 + 게이트 OFF(현재): 팁/포인트 모두 숨김 + 어느 보정치도 미전송 (#654 fail-safe 유지).
 *   활성화: admin #1857 머지·배포 후 `SESSION_BUNDLE_TIP_ENABLED` 1곳만 true 로 전환.
 *
 * ## 재사용 자산 (중복 작성 회피 — ChargePaymentModal 미러)
 *   - 카드 입력 UI / Luhn·만료·CVV 검증 / PCI 메모리 폐기: `BloominCardInputForm`
 *     (initiate POST 대신 `onSubmitOverride` 로 세션 `/charge` 흐름 위임 — UI 단일 소스)
 *   - 팁/포인트 입력: checkout `TipSelector` / `PointsSection` 재사용
 *   - 사용 가능 포인트: `useAvailablePoints(storeId, isMember)`
 *   - challenge 신뢰 검증: `isTrustedBloominChallengeUrl`
 *   - API: `usePaymentApi().chargeSession` → snake→camel 정규화 응답 소비 (#1705 R8)
 *
 * ## 에러 처리 (ApiClientError.code 별 — 단건 모달과 동일 키 사용)
 *   - PAYMENT_NOT_SETTLEABLE (409) → 정산 불가 안내
 *   - PAYMENT_PROCESSING_CANNOT_CANCEL (409) → 처리 중 안내
 *   - ADJUSTMENT_VALIDATION_FAILED (422) → 보정치 검증 실패
 *   - CARD_REQUIRED_FOR_CHARGE (422) → 청구액>0 인데 카드 미입력 (방어)
 *   - 그 외 / 신뢰 실패 → generic / untrustedChallenge
 *
 * @see src/pages/SessionChargePage.tsx (#661 전용 페이지 — 본 패널 소비)
 * @see src/components/payment/ChargePaymentModal.tsx (단건 — 팁/포인트 패턴 원본)
 * @see src/lib/api/payments.ts tableSessionApi.chargeSession (snake→camel 정규화)
 * @see src/lib/payments/bloominChallenge.ts isTrustedBloominChallengeUrl
 * @see docs/plans/2026-06-04-postpay-table-order-ux-redesign-design.md §0(D)/§3 [4]/§4
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BloominCardInputForm } from './BloominCardInputForm';
import type {
  BloominCardInputLabels,
  BloominCardSubmitPayload,
} from './BloominCardInputForm';
import type { ChargeAdjustmentsLabels, ChargePaymentErrorLabels } from './ChargePaymentModal';
import { TipSelector } from '@/components/checkout/TipSelector';
import { PointsSection } from '@/components/checkout/PointsSection';
import {
  isTrustedBloominChallengeUrl,
  UntrustedChallengeError,
} from '@/lib/payments/bloominChallenge';
import { usePaymentApi } from '@/hooks/usePaymentApi';
import { useAvailablePoints } from '@/hooks/useAvailablePoints';
import { ApiClientError } from '@/lib/api/client';
import type {
  ChargeSessionAdjustment,
  ChargeSessionResponse,
  SessionBundleAdjustment,
} from '@/lib/api/types';
import type { TableSessionPaymentRequestMethod } from '@/lib/api/payments';
import { logger } from '@/utils/logger';
import { floorMoneyByRatio, subtractMoney } from '@/utils/moneyMinor';
import {
  persistPendingBillReceipt,
  persistSessionChargeResult,
} from '@/lib/billReceiptContext';
import { SESSION_BILLS_CHANGED_EVENT } from '@/lib/sessionStorageKeys';

/**
 * #1857 feature-gate — N건 합산결제 팁(session_adjustments) 활성화 스위치 (단일 지점).
 *
 * admin #1857(`session_adjustments: { tip_amount }` 수용 + 주문별 비례 배분)이 dev 에
 * 머지·배포되기 전까지 false 유지 — 켜기 전 전송하면 admin 검증이 거부하거나 silent drop 된다.
 * 활성화 시 이 상수만 true 로 바꾸면 N건 팁 입력 UI + session_adjustments 전송이 켜진다.
 */
export const SESSION_BUNDLE_TIP_ENABLED = true;

export interface SessionChargePanelProps {
  /** 테이블 세션 토큰 (sess_...) */
  token: string;
  /** 세션 미결제 후불 합계 (서버 권위 문자열 — 소계 + 팁/포인트 미리보기 기준) */
  totalAmount: string;
  /** Bill quote의 서버 권위 소계. 비율 팁은 totalAmount가 아니라 이 금액으로 계산한다. */
  subtotalAmount?: string;
  /** Bill quote에 이미 박제된 팁. 재시도 시 동일 값으로 초기화하고 새 선택은 이를 교체한다. */
  initialTipAmount?: string;
  /** exact Bill stale-write precondition (quote 응답 값을 그대로 전달). */
  expectedBillSubtotalMinor?: number;
  /** Comensal 대표 pay-all stale-write precondition. */
  expectedTotalMinor?: number;
  /** 미결제 후불 건수 (접근성/테스트용) */
  count: number;
  /**
   * 보정치를 부여할 단일 주문 ID (§0(D) — #654: 미결제 정확히 1건일 때만 orders[0].id).
   * `adjustments[String(adjustmentOrderId)] = {tip_amount, points_amount}` 로 전송된다.
   * N건이면 null — 팁/포인트 입력 UI 숨김 + adjustments 미전송 (fail-safe).
   */
  adjustmentOrderId: number | null;
  /** 매장 ID (사용 가능 포인트 조회 — 회원 한정) */
  storeId: number;
  /** 회원 여부 (false 면 게스트 — guest_password 동봉 + 포인트 disabled) */
  isMember: boolean;
  /** 비회원 주문 비밀번호 */
  guestPassword?: string;
  /** 지정 시 본인 Bill만 quote/charge/request 한다. */
  billId?: number;
  comensalDeviceToken?: string;
  /**
   * #1857 N건 팁 게이트 override (테스트 전용 — 운영 코드는 미주입 = SESSION_BUNDLE_TIP_ENABLED).
   * 활성화 스위치는 상수 1곳(SESSION_BUNDLE_TIP_ENABLED)이며 prop 은 게이트 경로 테스트 용도다.
   */
  bundleTipEnabled?: boolean;
  /** 매장 설정상 고객 앱 카드 후불 세션 청구가 가능한지 (`card_platform_postpay` table scope). */
  cardChargeAvailable?: boolean;
  /** 수동/현장 후불 세션에서 고객이 POS로 요청할 수 있는 결제 처리 수단. */
  manualPostpayRequestMethods?: TableSessionPaymentRequestMethod[];
  /** 결제수단 계약 조회 중이면 카드/현금 상태를 확정하지 않고 로딩 placeholder 를 노출한다. */
  isResolvingCardChargeAvailability?: boolean;
  /**
   * 결제가 challenge 없이 즉시 성공 시 호출 — 주문 내역 refetch + 주문내역 복귀 트리거.
   *
   * 정상 흐름(3DS challenge redirect)은 페이지를 떠나므로 호출되지 않는다.
   * 0원 번들(보정치 전액 커버) 등 challenge 없는 즉시 성공 시에만 호출된다.
   */
  onSuccess: () => void;
  /** 409 BILL_QUOTE_STALE 시 bills/quote를 새로 읽게 한다. 자동 재청구는 금지한다. */
  onQuoteStale?: () => void;
  /** i18n labels (호출자가 15언어 session/payNow 키 + Bloomin 카드 폼 라벨 합성해 주입) */
  labels: {
    title: string;
    description: string;
    paymentMethodResolutionError: string;
    cardInput: BloominCardInputLabels;
    adjustments: ChargeAdjustmentsLabels;
    errors: ChargePaymentErrorLabels;
    manualPostpayNotice?: {
      title: string;
      body: string;
      adjustmentsPolicy?: string;
      /** 카드 오류 뒤 현장 요청은 온라인 tip/points를 제외한 서버 quote 금액임을 명시한다. */
      requestAmountLabel?: string;
      /** 개인 Bill 현장 요청도 화면의 선택 팁/합계를 그대로 사용한다는 안내. */
      billTipPolicy?: string;
      requestButton: string;
      requested: string;
      posCardReaderTitle?: string;
      posCardReaderBody?: string;
      posCardReaderRequestButton?: string;
      posCardReaderRequested?: string;
    };
    splitBillNotice?: {
      refundPolicy: string;
      remainingLabel: string;
    };
  };
}

const FALLBACK_MANUAL_POSTPAY_NOTICE = {
  title: 'Pago en caja',
  body: 'Esta mesa está configurada para pago en efectivo en tienda. Pide al personal que confirme el cobro.',
  adjustmentsPolicy: 'Las propinas se manejan directamente con el personal y los puntos no están disponibles para este método de pago en tienda.',
  requestAmountLabel: 'Monto solicitado en caja (sin propina ni puntos)',
  billTipPolicy: 'La solicitud en caja usará el mismo total mostrado, incluida la propina seleccionada. Los puntos no están disponibles.',
  requestButton: 'Solicitar pago en efectivo',
  requested: 'Solicitud enviada al POS.',
  posCardReaderTitle: 'Pago con terminal',
  posCardReaderBody: 'Esta mesa está configurada para pago con terminal de tarjeta en tienda. Pide al personal que lleve la terminal.',
  posCardReaderRequestButton: 'Solicitar terminal de tarjeta',
  posCardReaderRequested: 'Solicitud enviada al POS.',
};

/**
 * 팁 금액 계산 — ChargePaymentModal `computeTipAmount` 미러.
 *
 * base(=세션 소계) × selectedTip% 또는 custom(% / 금액). 소수 2자리 반올림.
 */
function computeTipAmount(
  base: number,
  selectedTip: number,
  customTip: string,
  customTipMode: 'percentage' | 'amount'
): number {
  if (selectedTip > 0) {
    return Math.round(base * (selectedTip / 100) * 100) / 100;
  }

  if (customTip) {
    const value = parseFloat(customTip) || 0;
    if (customTipMode === 'percentage') {
      return Math.round(base * (value / 100) * 100) / 100;
    }
    return Math.round(value * 100) / 100;
  }

  return 0;
}

/**
 * ApiClientError.code → 사용자 노출 메시지 매핑 (ChargePaymentModal 미러).
 *
 * 알 수 없는 code 는 generic 으로 통일 (PII / 내부 정보 노출 회피).
 */
function resolveChargeErrorMessage(
  error: unknown,
  errors: ChargePaymentErrorLabels
): string {
  if (error instanceof ApiClientError) {
    switch (error.code) {
      case 'PAYMENT_NOT_SETTLEABLE':
        return errors.notSettleable;
      case 'PAYMENT_PROCESSING_CANNOT_CANCEL':
        return errors.processingCannotCancel;
      case 'ADJUSTMENT_VALIDATION_FAILED':
        return errors.adjustmentValidationFailed;
      case 'CARD_REQUIRED_FOR_CHARGE':
        return errors.cardRequiredForCharge;
      case 'BILL_QUOTE_STALE':
        return errors.quoteStale ?? 'The bill amount changed. Review the updated amount and try again.';
      default:
        return errors.generic;
    }
  }

  return errors.generic;
}

export function SessionChargePanel({
  token,
  totalAmount,
  subtotalAmount,
  initialTipAmount,
  expectedBillSubtotalMinor,
  expectedTotalMinor,
  count,
  adjustmentOrderId,
  storeId,
  isMember,
  guestPassword,
  billId,
  comensalDeviceToken,
  bundleTipEnabled = SESSION_BUNDLE_TIP_ENABLED,
  cardChargeAvailable = true,
  manualPostpayRequestMethods = ['cash'],
  isResolvingCardChargeAvailability = false,
  onSuccess,
  onQuoteStale,
  labels,
}: SessionChargePanelProps) {
  // Codex Important 1 (defense-in-depth): 회원이면 guest_password 미전송.
  // 호출자(SessionChargePage)가 이미 회원 시 undefined 를 주입하나, 패널 자체에서도 차단해
  // 잘못된 호출(회원 + guestPassword 동시 주입)에도 회원 모드 일관성을 보장한다.
  const effectiveGuestPassword = isMember ? undefined : guestPassword;
  const { chargeSession, requestPaymentRequest } = usePaymentApi({
    guestPassword: billId === undefined ? effectiveGuestPassword : undefined,
    billId,
    comensalDeviceToken,
    expectedBillSubtotalMinor,
    expectedTotalMinor,
  });
  const [chargeError, setChargeError] = useState<string | null>(null);
  const [paymentRequestStates, setPaymentRequestStates] = useState<Record<TableSessionPaymentRequestMethod, 'idle' | 'submitting' | 'sent'>>({
    cash: 'idle',
    pos_card_reader: 'idle',
  });

  // 보정치 상태 (#603 [4] — ChargePaymentModal 미러, 쿠폰 제외).
  const [selectedTip, setSelectedTip] = useState<number>(0);
  const [customTip, setCustomTip] = useState<string>('');
  const [customTipMode, setCustomTipMode] = useState<'percentage' | 'amount'>('percentage');
  const [pointsToUse, setPointsToUse] = useState<number>(0);
  const [isConfirmingZero, setIsConfirmingZero] = useState(false);

  // 세션 소계 — 서버 권위 문자열을 숫자로 (미리보기 계산 기준). 파싱 불가 시 0(fail-closed).
  const sessionSubtotal = useMemo(() => {
    const numeric = Number(subtotalAmount ?? totalAmount);
    return Number.isFinite(numeric) ? numeric : 0;
  }, [subtotalAmount, totalAmount]);

  // #654: per-order 보정치 입력 가능 여부 — 단일 주문일 때만(adjustmentOrderId 결정 가능).
  // N건이면 포인트 UI 숨김 + per-order adjustments 미전송 (귀속 주문 결정 불가 — fail-safe).
  const canAdjust = cardChargeAvailable
    && billId === undefined
    && comensalDeviceToken === undefined
    && adjustmentOrderId !== null;
  // #1857: N건 세션(번들) 팁 — admin 계약 배포 전까지 게이트 OFF (구조만 준비).
  const isBundleTip = (cardChargeAvailable || billId !== undefined)
    && !canAdjust
    && bundleTipEnabled;
  // 팁 입력 가능 = 단건(per-order) 또는 N건 게이트 ON(session_adjustments).
  // exact Bill의 현장 결제도 tip_amount를 서버에 박제하므로 카드 지원 여부와 무관하게
  // 같은 팁 선택/합계를 사용한다. legacy/host cash-only는 기존처럼 팁을 열지 않는다.
  const canTip = (cardChargeAvailable || billId !== undefined) && (canAdjust || isBundleTip);

  // 사용 가능 포인트 — 회원 + 보정치 입력 가능 시에만 조회(불필요 API 회피).
  // 모달의 isOpen 게이트는 페이지 마운트로 대체(마운트 = 표시 중).
  const { data: pointsData } = useAvailablePoints(storeId, isMember && canAdjust);

  // #654 codex I1: 금액 계산도 입력 가능 여부(canTip)로 게이팅 — build* 만 undefined 면
  // 숨겨진 stale tip/points state 가 미리보기/0원 분기에 남아 표시 금액과 실제 payload 가
  // 불일치할 수 있다(단건→N건 전환 등). 아래 reset effect 와 이중 방어.
  const tipAmount = useMemo(
    () =>
      canTip
        ? computeTipAmount(sessionSubtotal, selectedTip, customTip, customTipMode)
        : 0,
    [canTip, sessionSubtotal, selectedTip, customTip, customTipMode]
  );

  const effectivePointsToUse = canAdjust ? pointsToUse : 0;

  // 청구액 미리보기 = max(0, 소계 + 팁 − 포인트). 서버가 재계산·권위.
  // 통화 2자리 정규화 — 부동소수 잔차가 0원 정확비교를 오판하지 않도록(ChargePaymentModal C1 정합).
  const chargeAmount = useMemo(
    () => subtractMoney(sessionSubtotal + tipAmount, effectivePointsToUse),
    [sessionSubtotal, tipAmount, effectivePointsToUse]
  );

  // 포인트 사용 상한 = floor(소계+팁 × maxUseRatio). gross+tip 기준(ChargePaymentModal 패턴).
  const maxPoints = useMemo(() => {
    if (!pointsData) {
      return undefined;
    }
    return floorMoneyByRatio(sessionSubtotal + tipAmount, pointsData.maxUseRatio);
  }, [pointsData, sessionSubtotal, tipAmount]);

  /**
   * #654 codex I1: 보정치 입력 불가 전환 시 tip/points state 자체를 reset.
   *
   * 페이지가 열린 채 단건→N건으로 전환되면(refetch 등) 숨겨진 입력값이 state 에 잔존한다.
   * 위의 canTip/canAdjust 게이팅으로 계산엔 이미 미반영이지만, state 도 비워 단건 복귀 시
   * 의도치 않은 과거 입력이 되살아나는 것까지 차단한다.
   * #1857: 팁은 canTip(N건 게이트 ON 이면 유지), 포인트는 canAdjust(단건 한정) 기준으로 분리.
   */
  useEffect(() => {
    if (!canTip) {
      setSelectedTip(0);
      setCustomTip('');
      setCustomTipMode('percentage');
    }
  }, [canTip]);

  useEffect(() => {
    if (!canTip || billId === undefined) return;
    const numeric = Number(initialTipAmount ?? 0);
    const normalized = Number.isFinite(numeric) && numeric > 0 ? String(numeric) : '';
    setSelectedTip(0);
    setCustomTip(normalized);
    setCustomTipMode('amount');
  }, [billId, canTip, initialTipAmount]);

  useEffect(() => {
    if (!canAdjust) {
      setPointsToUse(0);
    }
  }, [canAdjust]);

  const formatCurrency = useCallback((value: number): string => {
    // 표시 통화는 MXN 고정 (멕시코 후불 매장). ChargePaymentModal 정책 정합.
    if (!Number.isFinite(value)) {
      return String(value);
    }
    return `MX$${new Intl.NumberFormat('es-MX', { minimumFractionDigits: 2 }).format(value)}`;
  }, []);

  /**
   * 단일 order 보정치 동봉 (§0(D)) — `adjustments[String(adjustmentOrderId)] = {tip,points}`.
   *
   * 비례배분 없이 단일 order 전액. 0 은 전송 생략(서버 default 정합). 회원만 포인트 전송
   * (게스트 PointsSection disabled). 보정치가 전혀 없으면 adjustments 자체를 생략한다(undefined).
   *
   * #654: 실제 단건 주문 보정이 가능한 `canAdjust`가 아니면 항상 undefined — Bill 결제는
   * quote에 주문 하나가 보여도 팁 귀속이 Bill 전체이므로 per-order와 session 보정을 함께
   * 보내면 안 된다. 임의 첫 주문 부여(silent)도 코드 레벨에서 차단한다.
   */
  const buildAdjustments = useCallback(():
    | Record<string, ChargeSessionAdjustment>
    | undefined => {
    if (!canAdjust || adjustmentOrderId === null) {
      return undefined;
    }
    const entry: ChargeSessionAdjustment = {};
    if (tipAmount > 0) {
      entry.tip_amount = tipAmount;
    }
    if (isMember && pointsToUse > 0) {
      entry.points_amount = pointsToUse;
    }
    if (Object.keys(entry).length === 0) {
      return undefined;
    }
    return { [String(adjustmentOrderId)]: entry };
  }, [canAdjust, tipAmount, isMember, pointsToUse, adjustmentOrderId]);

  /**
   * #1857: 세션(번들) 단위 팁 동봉 — N건 + 게이트 ON 일 때만 `session_adjustments.tip_amount`.
   *
   * per-order `adjustments` 와 **배타**: buildAdjustments 는 canAdjust=false(N건 또는 Bill)이면
   * 항상 undefined, 본 함수는 단건(canAdjust)이면 항상 undefined — 동시 전송이 구조적으로 불가.
   * 게이트 OFF(현재 기본)면 N건에서 어느 보정치도 전송하지 않는다(#654 fail-safe 유지).
   */
  const buildSessionAdjustments = useCallback(():
    | SessionBundleAdjustment
    | undefined => {
    if (!isBundleTip || tipAmount <= 0) {
      return undefined;
    }
    return { tip_amount: tipAmount };
  }, [isBundleTip, tipAmount]);

  /**
   * challenge 검증 + redirect 공통 처리 — 단건 모달과 동일.
   *
   * challenge 가 있으면 신뢰 검증 후 redirect(페이지 이탈), 없으면 onSuccess().
   * 신뢰 실패는 untrustedChallenge 에러를 throw 한다.
   */
  const handleChargeResult = useCallback(
    (result: ChargeSessionResponse): void => {
      const challengeUrl = result.bloomin?.challengeUrl;

      if (challengeUrl) {
        let parsed: URL;
        try {
          parsed = new URL(challengeUrl);
        } catch {
          logger.error('[SessionChargePanel] challenge URL parse 실패');
          throw new UntrustedChallengeError(labels.errors.untrustedChallenge);
        }

        if (!isTrustedBloominChallengeUrl(parsed)) {
          logger.error('[SessionChargePanel] challenge URL 차단: 신뢰 불가 host');
          throw new UntrustedChallengeError(labels.errors.untrustedChallenge);
        }

        // 서버 청구가 생성됐어도 신뢰할 수 없는 redirect면 결과 컨텍스트를 남기지 않는다.
        // 검증된 URL로 실제 이탈하기 직전에만 동일 Comensal의 복구 bearer를 보관한다.
        if (comensalDeviceToken !== undefined) {
          persistSessionChargeResult(
            token,
            result.bundle.id,
            comensalDeviceToken,
            billId,
          );
        }
        if (billId !== undefined) persistPendingBillReceipt(token, billId);

        logger.info('[SessionChargePanel] Bloomin 3DS redirect 시작:', {
          bundleId: result.bundle.id,
          orderCount: result.bundle.order_ids.length,
          reference3D: result.bloomin?.reference3D,
        });
        window.location.assign(parsed.toString());

        return;
      }

      // challenge 없는 즉시 성공 (0원 번들 등) — 주문 내역 refetch.
      if (billId !== undefined) persistPendingBillReceipt(token, billId);
      onSuccess();
    },
    [billId, comensalDeviceToken, labels.errors.untrustedChallenge, onSuccess, token]
  );

  /**
   * 카드 제출 — BloominCardInputForm 검증 통과 후 정규화 payload 수신 (청구액 > 0).
   *
   * 금액 합계는 서버 권위(클라 미전송). 보정치(팁/포인트)만 단일 order adjustments 로 동봉.
   * 에러는 throw 하여 BloominCardInputForm 의 onError → `handleFormError` 가 패널 상단 alert 에 표시.
   */
  const handleCardSubmit = useCallback(
    async (payload: BloominCardSubmitPayload): Promise<void> => {
      setChargeError(null);

      let result: ChargeSessionResponse;
      try {
        result = await chargeSession(token, {
          tarjeta: payload.tarjeta,
          mes: payload.mes,
          anio: payload.anio,
          digitos: payload.digitos,
          titular: payload.titular,
          guest_password: effectiveGuestPassword,
          adjustments: buildAdjustments(),
          session_adjustments: buildSessionAdjustments(),
        });
      } catch (error) {
        if (error instanceof ApiClientError && error.code === 'BILL_QUOTE_STALE') {
          onQuoteStale?.();
        }
        if (error instanceof ApiClientError) {
          throw new Error(resolveChargeErrorMessage(error, labels.errors));
        }

        throw new Error(labels.errors.generic);
      }

      // 서버가 결제를 만든 뒤, challenge 이동 전에 복구 단서를 저장한다. 요청 자체가
      // 실패했는데 미결제 Bill을 영수증 polling 대상으로 남기는 stale context를 피한다.
      handleChargeResult(result);
    },
    [
      buildAdjustments,
      buildSessionAdjustments,
      chargeSession,
      effectiveGuestPassword,
      handleChargeResult,
      labels.errors,
      onQuoteStale,
      token,
    ]
  );

  /**
   * 0원 cardless 확정 (청구액 === 0) — 빈 카드 5필드 + 보정치만 전송.
   *
   * admin 이 0원 번들이면 카드 미요구 → 즉시 finalize (challenge 없음).
   */
  const handleZeroChargeConfirm = useCallback(async (): Promise<void> => {
    setChargeError(null);
    setIsConfirmingZero(true);

    try {
      const result = await chargeSession(token, {
        tarjeta: '',
        mes: '',
        anio: '',
        digitos: '',
        titular: '',
        guest_password: effectiveGuestPassword,
        adjustments: buildAdjustments(),
        session_adjustments: buildSessionAdjustments(),
      });

      handleChargeResult(result);
    } catch (error) {
      if (error instanceof ApiClientError && error.code === 'BILL_QUOTE_STALE') {
        onQuoteStale?.();
      }
      const message =
        error instanceof ApiClientError
          ? resolveChargeErrorMessage(error, labels.errors)
          : error instanceof UntrustedChallengeError
            ? labels.errors.untrustedChallenge
            : labels.errors.generic;
      logger.error('[SessionChargePanel] 0원 확정 실패:', message);
      setChargeError(message);
    } finally {
      setIsConfirmingZero(false);
    }
  }, [
    buildAdjustments,
    buildSessionAdjustments,
    chargeSession,
    effectiveGuestPassword,
    handleChargeResult,
    labels.errors,
    onQuoteStale,
    token,
  ]);

  const handleFormError = useCallback((message: string): void => {
    // BloominCardInputForm already reports this failure without sensitive details.
    setChargeError(message);
  }, []);

  const handleManualPaymentRequest = useCallback(async (method: TableSessionPaymentRequestMethod): Promise<void> => {
    setChargeError(null);
    setPaymentRequestStates((current) => ({ ...current, [method]: 'submitting' }));

    try {
      await requestPaymentRequest(token, method, billId === undefined ? undefined : tipAmount);
      if (billId !== undefined) persistPendingBillReceipt(token, billId);
      setPaymentRequestStates((current) => ({ ...current, [method]: 'sent' }));
      // 서버가 Bill을 en_caja로 바꿨으므로 현재 화면의 진행 상태를 즉시 다시 읽는다.
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event(SESSION_BILLS_CHANGED_EVENT));
      }
    } catch (error) {
      logger.error('[SessionChargePanel] 현장 결제 요청 실패:', error);
      if (error instanceof ApiClientError && error.code === 'BILL_QUOTE_STALE') {
        onQuoteStale?.();
      }
      setPaymentRequestStates((current) => ({ ...current, [method]: 'idle' }));
      setChargeError(resolveChargeErrorMessage(error, labels.errors));
    }
  }, [billId, labels.errors, onQuoteStale, requestPaymentRequest, tipAmount, token]);

  const manualNotice = labels.manualPostpayNotice ?? FALLBACK_MANUAL_POSTPAY_NOTICE;
  const primaryManualRequestMethod = manualPostpayRequestMethods.includes('pos_card_reader') && !manualPostpayRequestMethods.includes('cash')
    ? 'pos_card_reader'
    : 'cash';
  const manualPostpayTitle = primaryManualRequestMethod === 'pos_card_reader'
    ? (manualNotice.posCardReaderTitle ?? FALLBACK_MANUAL_POSTPAY_NOTICE.posCardReaderTitle)
    : manualNotice.title;
  const manualPostpayBody = primaryManualRequestMethod === 'pos_card_reader'
    ? (manualNotice.posCardReaderBody ?? FALLBACK_MANUAL_POSTPAY_NOTICE.posCardReaderBody)
    : manualNotice.body;
  const manualAdjustmentsPolicy = billId === undefined
    ? (manualNotice.adjustmentsPolicy ?? FALLBACK_MANUAL_POSTPAY_NOTICE.adjustmentsPolicy)
    : (manualNotice.billTipPolicy ?? FALLBACK_MANUAL_POSTPAY_NOTICE.billTipPolicy);
  const manualRequestLabels = useMemo(
    () => ({
      cash: {
        button: manualNotice.requestButton,
        requested: manualNotice.requested,
        testId: 'cash-payment-request-button',
      },
      pos_card_reader: {
        button: manualNotice.posCardReaderRequestButton ?? FALLBACK_MANUAL_POSTPAY_NOTICE.posCardReaderRequestButton,
        requested: manualNotice.posCardReaderRequested ?? FALLBACK_MANUAL_POSTPAY_NOTICE.posCardReaderRequested,
        testId: 'pos-card-reader-payment-request-button',
      },
    }),
    [manualNotice],
  );
  const manualPaymentRequestButtons = (
    <div className="space-y-[8px]">
      {manualPostpayRequestMethods.map((method) => (
        <button
          key={method}
          type="button"
          data-testid={manualRequestLabels[method].testId}
          onClick={() => void handleManualPaymentRequest(method)}
          disabled={paymentRequestStates[method] !== 'idle'}
          className="h-[44px] w-full rounded-[8px] bg-primary px-[14px] text-[15px] font-semibold text-white transition-colors hover:bg-primary-hover disabled:opacity-60"
        >
          {paymentRequestStates[method] === 'sent'
            ? manualRequestLabels[method].requested
            : manualRequestLabels[method].button}
        </button>
      ))}
    </div>
  );

  // 0원 분기 판정 — epsilon 으로 견고화(미리보기 "0.00" 과 카드생략 분기 동시 정합).
  const isZeroCharge = chargeAmount < 0.005;

  return (
    <div data-testid="session-charge-panel" className="space-y-5">
      {chargeError && (
        <div className="space-y-[10px]">
          <p
            role="alert"
            className="text-[13px] text-destructive bg-destructive/10 rounded-[8px] px-[12px] py-[8px]"
          >
            {chargeError}
          </p>
          {billId === undefined && cardChargeAvailable && manualPostpayRequestMethods.length > 0 ? (
            <div
              data-testid="card-charge-fallback-payment-requests"
              className="rounded-[10px] border border-primary/20 bg-primary/5 p-[12px] space-y-[8px]"
            >
              <p className="text-[13px] leading-5 text-muted-foreground">
                {manualPostpayBody}
              </p>
              <p
                className="text-[13px] font-medium text-foreground"
                data-testid="manual-fallback-payment-amount"
              >
                {manualNotice.requestAmountLabel
                  ?? FALLBACK_MANUAL_POSTPAY_NOTICE.requestAmountLabel}:{' '}
                <span className="tabular-nums">{formatCurrency(sessionSubtotal)}</span>
              </p>
              {manualPaymentRequestButtons}
            </div>
          ) : null}
        </div>
      )}

      {/* 테스트/접근성용 건수 노출 (시각적으로는 페이지 description 이 담당) */}
      <span className="sr-only" data-testid="session-charge-count">
        {count}
      </span>

      {billId !== undefined ? (
        <section
          className="rounded-[10px] border border-border bg-muted/50 p-[14px] text-[13px] leading-5 text-foreground"
          data-testid="split-bill-refund-notice"
          role="note"
        >
          <p>
            {labels.splitBillNotice?.refundPolicy
              ?? 'Esta cuenta quedará confirmada; cambiar la división después requerirá un reembolso y un nuevo cobro.'}
          </p>
          <p className="mt-1 font-medium" data-testid="split-bill-remaining-before-payment">
            {labels.splitBillNotice?.remainingLabel ?? 'Saldo de esta cuenta'}:{' '}
            {formatCurrency(sessionSubtotal)}
          </p>
        </section>
      ) : null}

      {/* 식후 정산 보정치 (#603 [4]) — 팁/포인트 (쿠폰 제외).
          #654: N건이면 per-order 보정치 불가 — 포인트는 숨김(귀속 주문 결정 불가, 임의 첫 주문
          부여 silent 동작 금지). #1857: 팁은 N건 게이트 ON 이면 세션 단위로 입력 가능. */}
      {canTip && (
        <section className="space-y-4" aria-label={labels.adjustments.sectionTitle}>
          <TipSelector
            selectedTip={selectedTip}
            customTip={customTip}
            customTipMode={customTipMode}
            onSelectTip={setSelectedTip}
            onCustomTip={(value, mode) => {
              setCustomTip(value);
              setCustomTipMode(mode);
            }}
            tipAmount={tipAmount}
            label={labels.adjustments.tip.label}
            customLabel={labels.adjustments.tip.customLabel}
            tipAmountLabel={labels.adjustments.tip.amountLabel}
            formatCurrency={formatCurrency}
            calculateSubtotal={() => sessionSubtotal}
            modalT={{
              enterTip: labels.adjustments.tip.enterTip,
              percentage: labels.adjustments.tip.percentage,
              amount: labels.adjustments.tip.amount,
              enterTipPercentage: labels.adjustments.tip.enterTipPercentage,
              enterTipAmount: labels.adjustments.tip.enterTipAmount,
              estimatedTip: labels.adjustments.tip.estimatedTip,
              cancel: labels.adjustments.tip.cancel,
              apply: labels.adjustments.tip.apply,
            }}
          />

          {canAdjust && (
          <PointsSection
            disabled={!isMember || !pointsData}
            totalAvailable={pointsData?.totalAvailable ?? 0}
            usableAmount={pointsData?.usableAmount ?? 0}
            minUsePoints={pointsData?.minUsePoints ?? 0}
            maxPoints={maxPoints}
            onPointsChange={setPointsToUse}
            pointsToUse={pointsToUse}
            formatCurrency={formatCurrency}
            loginPromptLabel={!isMember ? labels.adjustments.points.loginPrompt : undefined}
            labels={{
              sectionTitle: labels.adjustments.points.sectionTitle,
              available: labels.adjustments.points.available,
              usable: labels.adjustments.points.usable,
              placeholder: labels.adjustments.points.placeholder,
              apply: labels.adjustments.points.apply,
              useAll: labels.adjustments.points.useAll,
              discountApplied: labels.adjustments.points.discountApplied,
              minAmount: labels.adjustments.points.minAmount,
            }}
          />
          )}
        </section>
      )}

      {/* 청구액 미리보기 (서버 권위 — FE 미리보기일 뿐) */}
      <section
        className="rounded-[10px] bg-muted p-[16px] space-y-[6px] text-[13px]"
        aria-label={labels.adjustments.preview.title}
        data-testid="charge-preview"
      >
        <div className="flex justify-between text-muted-foreground">
          <span>{labels.adjustments.preview.orderTotal}</span>
          <span className="text-foreground">{formatCurrency(sessionSubtotal)}</span>
        </div>
        {tipAmount > 0 && (
          <div className="flex justify-between text-muted-foreground">
            <span>{labels.adjustments.preview.tip}</span>
            <span className="text-foreground">+{formatCurrency(tipAmount)}</span>
          </div>
        )}
        {/* I1: 미리보기 행도 게이팅된 값 사용 — chargeAmount 와 같은 출처(표시-계산 정합). */}
        {effectivePointsToUse > 0 && (
          <div className="flex justify-between text-muted-foreground">
            <span>{labels.adjustments.preview.points}</span>
            <span className="text-primary">-{formatCurrency(effectivePointsToUse)}</span>
          </div>
        )}
        <div className="flex justify-between pt-[6px] mt-[2px] border-t border-border font-semibold text-[15px]">
          <span className="text-foreground">{labels.adjustments.preview.chargeAmount}</span>
          <span className="text-foreground" data-testid="charge-amount">
            {formatCurrency(chargeAmount)}
          </span>
        </div>
      </section>

      {/* 결제 입력 — 카드 후불 허용 시 청구액>0 카드 폼 / ==0 전액 확정, 현금 후불 전용이면 안내만 노출 */}
      {isResolvingCardChargeAvailability ? (
        <div
          data-testid="session-card-availability-loading"
          className="h-[72px] rounded-[10px] bg-muted animate-pulse"
          aria-busy="true"
        />
      ) : !cardChargeAvailable ? (
        <section
          data-testid="manual-postpay-notice"
          role="status"
          className="rounded-[10px] border border-primary/20 bg-primary/5 p-[14px] space-y-[4px]"
        >
          <h3 className="text-[15px] font-semibold text-foreground">
            {manualPostpayTitle}
          </h3>
          <p className="text-[13px] leading-5 text-muted-foreground">
            {manualPostpayBody}
          </p>
          {billId !== undefined ? (
            <p
              className="text-[13px] font-medium text-foreground"
              data-testid="bill-manual-payment-amount"
            >
              {formatCurrency(chargeAmount)}
            </p>
          ) : null}
          {manualAdjustmentsPolicy && (
            <p
              data-testid="manual-postpay-adjustments-policy"
              className="text-[13px] leading-5 text-muted-foreground"
            >
              {manualAdjustmentsPolicy}
            </p>
          )}
          <div className="mt-[10px]">
            {manualPaymentRequestButtons}
          </div>
        </section>
      ) : isZeroCharge ? (
        <button
          type="button"
          data-testid="zero-charge-confirm"
          onClick={() => void handleZeroChargeConfirm()}
          disabled={isConfirmingZero}
          className="w-full h-[52px] rounded-[8px] bg-primary text-white text-[16px] font-bold disabled:opacity-50 hover:bg-primary-hover transition-colors"
        >
          {labels.adjustments.zeroChargeConfirm}
        </button>
      ) : (
        <BloominCardInputForm
          initiateUrl=""
          guestPassword={effectiveGuestPassword}
          labels={labels.cardInput}
          onSubmitOverride={handleCardSubmit}
          onSuccess={() => {
            /* override 흐름에서는 호출되지 않음 (initiate 전용 callback) */
          }}
          onError={handleFormError}
        />
      )}

      {/* 개인 Bill은 매장 허용 범위 안에서 플랫폼 카드와 현장 결제를 사용자가 직접
          선택한다. legacy 세션 전체 결제는 기존처럼 카드 실패 뒤 fallback만 제공한다. */}
      {!isResolvingCardChargeAvailability
        && billId !== undefined
        && cardChargeAvailable
        && !isZeroCharge
        && manualPostpayRequestMethods.length > 0 ? (
          <section
            data-testid="bill-manual-payment-options"
            className="rounded-[10px] border border-primary/20 bg-primary/5 p-[14px] space-y-[4px]"
          >
            <h3 className="text-[15px] font-semibold text-foreground">
              {manualPostpayTitle}
            </h3>
            <p className="text-[13px] leading-5 text-muted-foreground">
              {manualPostpayBody}
            </p>
            <p
              className="text-[13px] font-medium text-foreground"
              data-testid="bill-manual-payment-amount"
            >
              {formatCurrency(chargeAmount)}
            </p>
            {manualAdjustmentsPolicy && (
              <p
                className="text-[13px] leading-5 text-muted-foreground"
                data-testid="manual-postpay-adjustments-policy"
              >
                {manualAdjustmentsPolicy}
              </p>
            )}
            <div className="pt-[6px]">
              {manualPaymentRequestButtons}
            </div>
          </section>
        ) : null}
    </div>
  );
}

export default SessionChargePanel;
